import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import type { Document } from '@contentful/rich-text-types'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'

import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import RichText from '@segment/matcha/recipes/RichText'

import streamlineCompliancePng2x from './assets/StreamlineCompliance@2x.png'
import streamlineComplianceWebp from './assets/StreamlineCompliance.webp'
import streamlineComplianceWebp2x from './assets/StreamlineCompliance@2x.webp'
import streamlineComplianceWebp3x from './assets/StreamlineCompliance@3x.webp'

const copy = {
  overline: `Subject Rights Management`,
  heading: `Streamline your regulatory compliance`,
}

const description = {
  content: [
    {
      content: [
        {
          data: {},
          marks: [],
          nodeType: `text`,
          value: `Accelerate compliance with regulations like the `,
        },
        {
          data: {
            uri: `https://segment.com/product/gdpr/`,
          },
          content: [
            {
              data: {},
              marks: [],
              value: `GDPR`,
              nodeType: `text`,
            },
          ],
          nodeType: INLINES.HYPERLINK,
        },
        {
          data: {},
          marks: [],
          nodeType: `text`,
          value: ` and the `,
        },
        {
          data: {
            uri: `https://segment.com/product/ccpa/`,
          },
          content: [
            {
              data: {},
              marks: [],
              value: `CCPA`,
              nodeType: `text`,
            },
          ],
          nodeType: INLINES.HYPERLINK,
        },
        {
          data: {},
          marks: [],
          nodeType: `text`,
          value: ` with automated data subject rights management and our `,
        },
        {
          data: {
            uri: `https://segment.com/blog/how-to-build-consent-management-into-your-site-in-less-than-a-week/`,
          },
          content: [
            {
              data: {},
              marks: [],
              value: `open source consent manager.`,
              nodeType: `text`,
            },
          ],
          nodeType: INLINES.HYPERLINK,
        },
      ],
      data: {},
      nodeType: BLOCKS.PARAGRAPH,
    },
    {
      content: [
        {
          data: {},
          marks: [],
          nodeType: `text`,
          value: `Whether your customers want you to delete or modify their data, or even stop collecting it entirely, Segment has you covered.`,
        },
      ],
      data: {},
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  data: {},
  nodeType: BLOCKS.DOCUMENT,
} as Document

const StreamlineCompliance: FC<PropsWithChildren<unknown>> = () => {
  return (
    <SectionBase variant="2Col" copyVariant="narrow" reverse={true}>
      <Section.Copy alignSelf="center">
        <CopyTextBase>
          <CopyText.Overline>{copy.overline}</CopyText.Overline>
          <CopyText.Heading tag="h3">{copy.heading}</CopyText.Heading>
          <RichText data={description} />
        </CopyTextBase>
      </Section.Copy>
      <SectionContents>
        <picture>
          <source
            type="image/webp"
            srcSet={`${streamlineComplianceWebp}, ${streamlineComplianceWebp2x} 2x, ${streamlineComplianceWebp3x} 3x`}
          />
          <Image
            width="640"
            height="560"
            src={streamlineCompliancePng2x}
            alt="Illustration: Streamline your regulatory compliance"
            loading="lazy"
          />
        </picture>
      </SectionContents>
    </SectionBase>
  )
}

const SectionContents = styled(Section.Contents)`
  justify-content: center;
  align-self: center;
  display: flex;
`

export default StreamlineCompliance
